import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { observer } from "mobx-react";
import { MainWrapper, MainContainer, CardsContainer, NoDataWrapper, NoDataTitle, NoDataDescription, PaginationContainer } from "./Main.styled";
import { Header, Filters, KnowledgeCard } from "./components";
import { Skeleton, Pagination } from "@ann/web-components";
import Icon from "../../../../../elements/icon/Icon";
import TrainModal from "../../components/TrainModal";
import UploadFactsModal from "../../components/UploadFactsModal";
import AIMSDriveModal from "../../components/AIMSDriveModal";
import { FactsTrainingModal } from "../KnowledgeHub/components";
import { listKnowledge } from "../../services/knowledge";
import { getUserByUuid } from "../../../../../services/users";
import { getDepartmentList } from "../../../../../services/departments";
import { useDebounce, useStore } from "../../../../../hooks";
import dayjs from "dayjs";

/**
 * Load departments from API
 * @returns {Promise<{departmentsMap: Map, departmentsList: Array}>}
 */
const loadDepartments = async () => {
  try {
    const response = await getDepartmentList({
      page: 1,
      page_size: 100, // Get all departments
      order_by: "created_at",
      order_direction: "DESC",
    });

    const departmentsMap = new Map();
    const departmentsList = [];
    
    if (response.body && Array.isArray(response.body)) {
      response.body.forEach((dept) => {
        // Support both camelCase (departmentName) and snake_case (department_name)
        // Also support department_uuid and departmentUuid
        const departmentName = dept.department_name || dept.departmentName;
        const departmentUuid = dept.department_uuid || dept.departmentUuid;
        if (departmentUuid && departmentName) {
          departmentsMap.set(departmentUuid, departmentName);
          departmentsList.push({
            uuid: departmentUuid,
            name: departmentName,
          });
        }
      });
    }
    
    return { departmentsMap, departmentsList };
  } catch (error) {
    console.error("Error loading departments from API:", error);
    return { departmentsMap: new Map(), departmentsList: [] };
  }
};

/**
 * Load users list from UsersStore for filter dropdown
 * @param {Object} usersStore - UsersStore instance
 * @returns {Promise<Array>} Array of users with uuid and fullName
 */
const loadUsersList = async (usersStore) => {
  try {
    await usersStore.getUserList({
      page: 1,
      page_size: 100, // Get all users
      order_by: "fullName",
      order_direction: "ASC",
    });

    const usersList = [];
    
    if (usersStore.users && Array.isArray(usersStore.users)) {
      usersStore.users.forEach((user) => {
        const fullName = user.fullName || user.full_name || "Unknown";
        const userUuid = user.user_uuid || user.userUuid || user.uuid;
        if (userUuid && fullName) {
          usersList.push({
            uuid: userUuid,
            name: fullName,
          });
        }
      });
    }
    
    return usersList;
  } catch (error) {
    console.error("Error loading users from UsersStore:", error);
    return [];
  }
};

/**
 * Load owners from API by their UUIDs
 * @param {string[]} ownerUuids - Array of unique owner UUIDs
 * @returns {Promise<Map<string, string>>} Map of owner_uuid -> fullName
 */
const loadOwners = async (ownerUuids) => {
  const ownersMap = new Map();
  
  if (!ownerUuids || ownerUuids.length === 0) {
    return ownersMap;
  }

  // Fetch all owners in parallel
  const promises = ownerUuids.map(async (uuid) => {
    try {
      const response = await getUserByUuid(uuid);
      const fullName = response.body?.fullName || "Unknown";
      return { uuid, fullName };
    } catch (error) {
      console.error(`Error fetching owner ${uuid}:`, error);
      return { uuid, fullName: "Unknown" };
    }
  });

  const results = await Promise.all(promises);
  results.forEach(({ uuid, fullName }) => {
    ownersMap.set(uuid, fullName);
  });

  return ownersMap;
};

/**
 * Load mock owners (fallback)
 */
const loadMockOwners = async () => {
  try {
    const response = await fetch("/mock_owners.json");
    const data = await response.json();
    const ownersMap = new Map();
    data.body?.forEach((owner) => {
      ownersMap.set(owner.owner_uuid, owner.name);
    });
    return ownersMap;
  } catch (error) {
    console.error("Error loading mock owners:", error);
    return new Map();
  }
};

/**
 * Transform API response data to component format
 * Uses defensive programming to handle both camelCase and snake_case
 */
const transformKnowledgeBase = (apiItem, departmentsMap, ownersMap) => {
  // Support both camelCase and snake_case (defensive programming)
  const knowledgebaseUuid = apiItem.knowledgebase_uuid || apiItem.knowledgebaseUuid;
  const departmentUuid = apiItem.department_uuid || apiItem.departmentUuid;
  const ownerUuid = apiItem.owner_uuid || apiItem.ownerUuid;
  const countMembers = apiItem.count_members || apiItem.countMembers || 0;
  const countFacts = apiItem.count_facts || apiItem.countFacts || 0;
  
  // Handle summary.updatedAt in both formats
  const summary = apiItem.summary || {};
  const updatedAt = summary.updated_at || summary.updatedAt;
  
  const departmentName = departmentsMap.get(departmentUuid) || "Unknown";
  const ownerName = ownersMap.get(ownerUuid) || "Unknown";

  return {
    id: knowledgebaseUuid,
    name: apiItem.name || "",
    description: apiItem.description || "",
    owner: ownerName,
    collaborators: countMembers,
    documents: countFacts,
    category: departmentName,
    status: apiItem.status?.toLowerCase() === "archived" ? "archived" : "active",
    lastModified: updatedAt 
      ? new Date(updatedAt).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0],
    // Keep the original ISO date for filtering
    updatedAt: updatedAt || new Date().toISOString(),
    // Keep UUIDs for reference (both formats)
    knowledgebase_uuid: knowledgebaseUuid,
    knowledgebaseUuid: knowledgebaseUuid,
    owner_uuid: ownerUuid,
    ownerUuid: ownerUuid,
    department_uuid: departmentUuid,
    departmentUuid: departmentUuid,
  };
};

/**
 * Load mock data from JSON file
 */
const loadMockData = async () => {
  try {
    const [{ departmentsMap }, ownersMap] = await Promise.all([
      loadDepartments(),
      loadMockOwners(),
    ]);

    const response = await fetch("/mock_knowledge_bases.json");
    const data = await response.json();
    return data.body?.map((item) => transformKnowledgeBase(item, departmentsMap, ownersMap)) || [];
  } catch (error) {
    console.error("Error loading mock data:", error);
    return [];
  }
};

const Main = observer(({ breadcrumbHelpers }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { usersStore } = useStore();
  const [loading, setLoading] = useState(false);
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [departmentsList, setDepartmentsList] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [selectedModified, setSelectedModified] = useState(null);
  const [orderBy, setOrderBy] = useState("name");
  const [orderDirection, setOrderDirection] = useState("asc");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [meta, setMeta] = useState(null);

  // Debounce search keyword (like ActivityLog)
  const debouncedSearchKeyword = useDebounce(searchKeyword, 500);
  
  // Track if search is pending
  const isSearching = searchKeyword !== debouncedSearchKeyword;
  
  // Track if filters are being applied
  const [isFiltering, setIsFiltering] = useState(false);
  const filterTimeoutRef = useRef(null);
  
  const [isTrainModalOpen, setIsTrainModalOpen] = useState(false);
  const [isUploadFactsModalOpen, setIsUploadFactsModalOpen] = useState(false);
  const [isAIMSDriveModalOpen, setIsAIMSDriveModalOpen] = useState(false);
  const [isFactsTrainingModalOpen, setIsFactsTrainingModalOpen] = useState(false);
  const [selectedKnowledgeBase, setSelectedKnowledgeBase] = useState(null);

  useEffect(() => {
    breadcrumbHelpers?.setPageBreadcrumb?.(null, null, { 
      pageHref: "/v2/knowledge" 
    });
  }, [breadcrumbHelpers]);

  useEffect(() => {
    if (location.state?.openCreateModal) {
      setIsCreateModalOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  // Fetch knowledge bases
  const fetchKnowledgeBases = useCallback(async () => {
    setLoading(true);
    try {
      // Load departments map and list from API
      const { departmentsMap, departmentsList } = await loadDepartments();
      setDepartmentsList(departmentsList);

      // Load users list from UsersStore for filter dropdown
      const usersListData = await loadUsersList(usersStore);
      setUsersList(usersListData);

      // Build filters for API (defensive programming)
      const filters = {};

      // Modified filter - send to backend
      if (selectedModified && Array.isArray(selectedModified) && selectedModified.length > 0) {
        const modValue = selectedModified[0];
        const value = typeof modValue === 'string' ? modValue : modValue?.value || modValue;
        
        if (value) {
          const now = dayjs();
          let modifiedFrom = null;
          
          switch (value) {
            case "last_7_days":
              modifiedFrom = now.subtract(7, 'day').startOf('day').toISOString();
              break;
            case "last_30_days":
              modifiedFrom = now.subtract(30, 'day').startOf('day').toISOString();
              break;
            case "this_year":
              modifiedFrom = now.startOf('year').toISOString();
              break;
          }
          
          if (modifiedFrom) {
            filters.modified_from = modifiedFrom;
          }
        }
      }

      // Department filter - TODO: map department name to UUID
      // if (selectedDepartment && Array.isArray(selectedDepartment) && selectedDepartment.length > 0) {
      //   const deptValue = selectedDepartment[0];
      //   const value = typeof deptValue === 'string' ? deptValue : deptValue?.value || deptValue;
      //   if (value) {
      //     // Find department UUID from departmentsList
      //     const department = departmentsList.find(d => 
      //       d.name.toLowerCase().replace(/\s+/g, "_") === value
      //     );
      //     if (department) {
      //       filters.department_uuid = department.uuid;
      //     }
      //   }
      // }

      // Owner filter - TODO: map owner name to UUID
      // if (selectedOwner && Array.isArray(selectedOwner) && selectedOwner.length > 0) {
      //   const ownerValue = selectedOwner[0];
      //   const value = typeof ownerValue === 'string' ? ownerValue : ownerValue?.value || ownerValue;
      //   if (value) {
      //     // Find owner UUID from usersList
      //     const user = usersListData.find(u => 
      //       u.name.toLowerCase().replace(/\s+/g, "_") === value
      //     );
      //     if (user) {
      //       filters.user_uuid = user.uuid;
      //     }
      //   }
      // }

      const apiFilters = Object.keys(filters).length > 0 ? filters : undefined;

      // Map orderBy to API field names
      const orderByMap = {
        name: "name",
        created: "created_at",
        modified: "updated_at",
      };
      const apiOrderBy = orderByMap[orderBy] || "updated_at";
      const apiOrderDirection = orderDirection.toUpperCase() === "ASC" ? "ASC" : "DESC";

      // Try to fetch knowledge bases from API
      let knowledgeBasesData = [];
      let responseMeta = null;
      try {
        const response = await listKnowledge({
          page: currentPage,
          page_size: itemsPerPage,
          order_by: apiOrderBy,
          order_direction: apiOrderDirection,
          filters: apiFilters,
        });

        if (response.body && Array.isArray(response.body)) {
          knowledgeBasesData = response.body;
        }
        
        // Save meta for pagination
        if (response.meta) {
          responseMeta = response.meta;
        }
      } catch (apiError) {
        console.error("Error fetching from API, loading mock:", apiError);
        // Load mock knowledge bases
        const mockResponse = await fetch("/mock_knowledge_bases.json");
        const mockData = await mockResponse.json();
        knowledgeBasesData = mockData.body || [];
      }

      // If no data from API, use mock
      if (knowledgeBasesData.length === 0) {
        const mockResponse = await fetch("/mock_knowledge_bases.json");
        const mockData = await mockResponse.json();
        knowledgeBasesData = mockData.body || [];
      }

      // Extract unique owner UUIDs (defensive programming - handle both formats)
      const ownerUuids = [...new Set(
        knowledgeBasesData
          .map((kb) => kb.owner_uuid || kb.ownerUuid)
          .filter((uuid) => uuid)
      )];

      // Load owners from API (with fallback to mock)
      let ownersMap;
      try {
        ownersMap = await loadOwners(ownerUuids);
      } catch (ownerError) {
        console.error("Error loading owners from API, using mock:", ownerError);
        ownersMap = await loadMockOwners();
      }

      // Transform knowledge bases with real owner names
      const transformed = knowledgeBasesData.map((item) => 
        transformKnowledgeBase(item, departmentsMap, ownersMap)
      );
      setKnowledgeBases(transformed);
      setMeta(responseMeta);

    } catch (error) {
      console.error("Error fetching knowledge bases, using mock data:", error);
      // On error, use mock data as fallback
      const mockData = await loadMockData();
      setKnowledgeBases(mockData);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  }, [selectedModified, currentPage, itemsPerPage, orderBy, orderDirection]);

  useEffect(() => {
    fetchKnowledgeBases();
  }, [fetchKnowledgeBases]);

  // Reset to page 1 when filters or sorting change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedModified, selectedDepartment, selectedOwner, debouncedSearchKeyword, orderBy, orderDirection]);

  // Show skeleton when filters change
  useEffect(() => {
    setIsFiltering(true);
    
    // Clear existing timeout
    if (filterTimeoutRef.current) {
      clearTimeout(filterTimeoutRef.current);
    }
    
    // Hide skeleton after a short delay to show filtering is complete
    filterTimeoutRef.current = setTimeout(() => {
      setIsFiltering(false);
    }, 300);
    
    return () => {
      if (filterTimeoutRef.current) {
        clearTimeout(filterTimeoutRef.current);
      }
    };
  }, [debouncedSearchKeyword, selectedDepartment, selectedOwner, selectedModified, orderBy, orderDirection]);

  // Filter and sort knowledge bases
  const filteredKnowledgeBases = useMemo(() => {
    let filtered = [...knowledgeBases];

    // Search filter (use debounced value)
    if (debouncedSearchKeyword && debouncedSearchKeyword.trim() !== "") {
      const searchLower = debouncedSearchKeyword.toLowerCase().trim();
      filtered = filtered.filter(
        (kb) =>
          kb.name.toLowerCase().includes(searchLower) ||
          kb.description.toLowerCase().includes(searchLower) ||
          kb.category.toLowerCase().includes(searchLower)
      );
    }

    // Department filter (now single selection with ButtonMenu)
    if (selectedDepartment && Array.isArray(selectedDepartment) && selectedDepartment.length > 0) {
      const deptValue = selectedDepartment[0];
      const value = typeof deptValue === 'string' ? deptValue : deptValue?.value || deptValue;
      if (value) {
        // The value from ButtonMenu is like "sales", "human_resources", etc.
        // Convert it back to readable format: "human_resources" -> "human resources"
        const deptLabel = String(value).replace(/_/g, " ").toLowerCase();
        filtered = filtered.filter((kb) => {
          const kbCategoryLower = kb.category.toLowerCase();
          // Exact match
          return kbCategoryLower === deptLabel;
        });
      }
    }

    // Owner filter (now single selection with ButtonMenu)
    if (selectedOwner && Array.isArray(selectedOwner) && selectedOwner.length > 0) {
      const ownerValue = selectedOwner[0];
      const value = typeof ownerValue === 'string' ? ownerValue : ownerValue?.value || ownerValue;
      if (value) {
        // The value from ButtonMenu is like "john_doe", "jane_smith", etc.
        // Convert it back: "john_doe" -> "john doe"
        const ownerLabel = String(value).replace(/_/g, " ").toLowerCase();
        filtered = filtered.filter((kb) => {
          const kbOwnerLower = kb.owner.toLowerCase();
          // Exact match
          return kbOwnerLower === ownerLabel;
        });
      }
    }

    // Modified filter is now handled by the backend API
    // No client-side filtering needed for Modified filter

    // Sorting is now handled by the backend API
    // No client-side sorting needed

    return filtered;
  }, [knowledgeBases, debouncedSearchKeyword, selectedDepartment, selectedOwner]);

  const handleTrain = useCallback((knowledgeBase) => {
    if (knowledgeBase) {
      setSelectedKnowledgeBase(knowledgeBase);
      setIsTrainModalOpen(true);
    }
  }, []);

  const handleTrainModalClose = useCallback(() => {
    setIsTrainModalOpen(false);
    setSelectedKnowledgeBase(null);
  }, []);

  const handleUploadDocuments = useCallback(() => {
    setIsTrainModalOpen(false);
    setIsUploadFactsModalOpen(true);
  }, []);

  const handleUploadFactsModalClose = useCallback(() => {
    setIsUploadFactsModalOpen(false);
    // Reopen TrainModal when closing UploadFactsModal (only if not uploading)
    setIsTrainModalOpen(true);
  }, []);

  const handleUploadStart = useCallback(() => {
    // Close both modals when upload starts
    setIsUploadFactsModalOpen(false);
    setIsTrainModalOpen(false);
  }, []);

  const handleAIMSDrive = useCallback(() => {
    setIsTrainModalOpen(false);
    setIsAIMSDriveModalOpen(true);
  }, []);

  const handleAIMSDriveModalClose = useCallback(() => {
    setIsAIMSDriveModalOpen(false);
    // Reopen TrainModal when closing AIMSDriveModal
    setIsTrainModalOpen(true);
  }, []);

  const handleContinueInterview = useCallback(() => {
    setIsTrainModalOpen(false);
    setIsFactsTrainingModalOpen(true);
  }, []);

  const handleFactsTrainingModalClose = useCallback(() => {
    setIsFactsTrainingModalOpen(false);
    setIsTrainModalOpen(true);
  }, []);

  const handleManage = useCallback((knowledgeBaseId, knowledgeBaseName) => {
    navigate(`/v2/knowledge/knowledge-hub/${knowledgeBaseId}`, {
      state: { knowledgeName: knowledgeBaseName },
    });
  }, [navigate]);

  const handleArchive = useCallback(() => {
    // Refresh knowledge bases list after archiving
    fetchKnowledgeBases();
  }, [fetchKnowledgeBases]);

  const handleCreateModalClose = useCallback(() => {
    setIsCreateModalOpen(false);
  }, []);

  const handleKnowledgeBaseCreated = useCallback(() => {
    fetchKnowledgeBases();
    setIsCreateModalOpen(false);
  }, [fetchKnowledgeBases]);

  const handleOpenCreateModal = useCallback(() => {
    setIsCreateModalOpen(true);
  }, []);

  const handleSortChange = useCallback((sortOption) => {
    // When changing sort field, maintain current order_direction or default to 'asc'
    const currentDirection = (orderDirection || 'asc').toLowerCase();
    setOrderBy(sortOption);
    setOrderDirection(currentDirection);
  }, [orderDirection]);

  const handleOrderChange = useCallback((currentOrder) => {
    // Toggle between 'asc' and 'desc'
    // currentOrder comes from SortMenu as 'asc' or 'desc' (lowercase)
    const normalizedOrder = (currentOrder || orderDirection || 'asc').toLowerCase();
    const newOrder = normalizedOrder === 'asc' ? 'desc' : 'asc';
    setOrderDirection(newOrder);
  }, [orderDirection]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = useCallback((itemsPerPage) => {
    setItemsPerPage(itemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  }, []);

  return (
    <MainWrapper>
      <Header 
        onKnowledgeBaseCreated={handleKnowledgeBaseCreated}
        onCreateModalOpen={isCreateModalOpen}
        onOpenCreateModal={handleOpenCreateModal}
        onCloseCreateModal={handleCreateModalClose}
        departmentsList={departmentsList}
      />
      <Filters
        onSearchChange={setSearchKeyword}
        onDepartmentChange={setSelectedDepartment}
        onOwnerChange={setSelectedOwner}
        onModifiedChange={setSelectedModified}
        onSortChange={handleSortChange}
        onOrderChange={handleOrderChange}
        selectedSortOption={orderBy}
        orderDirection={orderDirection}
        loading={loading}
        knowledgeBases={knowledgeBases}
        departmentsList={departmentsList}
        usersList={usersList}
        selectedDepartment={selectedDepartment}
        selectedOwner={selectedOwner}
        selectedModified={selectedModified}
        searchKeyword={searchKeyword}
      />
      <MainContainer>
        {(loading || isSearching || isFiltering) ? (
          <CardsContainer>
            {Array.from({ length: 6 }).map((_, index) => (
              <Skeleton key={`skeleton-${index}`} height="280px" width="100%" />
            ))}
          </CardsContainer>
        ) : filteredKnowledgeBases.length === 0 ? (
          <NoDataWrapper>
            <Icon name="noDataAvailable" width="201px" height="195px" />
            <NoDataTitle>No knowledge bases found</NoDataTitle>
            <NoDataDescription>
              {knowledgeBases.length === 0
                ? "No knowledge bases found. Create your first knowledge base to get started."
                : "No knowledge bases match your current filters. Try adjusting your search or filters."}
            </NoDataDescription>
          </NoDataWrapper>
        ) : (
          <CardsContainer>
            {filteredKnowledgeBases.map((knowledgeBase) => (
              <KnowledgeCard
                key={knowledgeBase.id}
                knowledgeBase={knowledgeBase}
                onTrain={handleTrain}
                onManage={handleManage}
                onArchive={handleArchive}
                onEditSuccess={fetchKnowledgeBases}
                departmentsList={departmentsList}
              />
            ))}
          </CardsContainer>
        )}
        {knowledgeBases.length > 0 && meta && (
          <PaginationContainer>
            <Pagination
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={handleItemsPerPageChange}
              onPageChange={handlePageChange}
              totalItems={meta.total_items || meta.totalItems || 0}
              totalPages={meta.total_pages || meta.totalPages || 1}
            />
          </PaginationContainer>
        )}
      </MainContainer>
      <TrainModal
        isOpen={isTrainModalOpen}
        onClose={handleTrainModalClose}
        knowledgeBase={selectedKnowledgeBase}
        onUploadDocuments={handleUploadDocuments}
        onAIMSDrive={handleAIMSDrive}
        onContinueInterview={handleContinueInterview}
      />
      <FactsTrainingModal
        isOpen={isFactsTrainingModalOpen}
        onClose={handleFactsTrainingModalClose}
      />
      <UploadFactsModal
        isOpen={isUploadFactsModalOpen}
        onClose={handleUploadFactsModalClose}
        knowledgeBase={selectedKnowledgeBase}
        onUploadStart={handleUploadStart}
      />
      <AIMSDriveModal
        isOpen={isAIMSDriveModalOpen}
        onClose={handleAIMSDriveModalClose}
        knowledgeBase={selectedKnowledgeBase}
      />
    </MainWrapper>
  );
});

export default Main;

